import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Logo from './Logo';
import UnitSwitcher from './UnitSwitcher';
import { useAuth } from '../lib/auth';

// Authenticated layout. Sticky branded header with the unit switcher front-and-
// center, body container, and a slim footer. Pages slot into the <main>.
// Language switch + Sign out live on the Profile page (keeps the header lean,
// especially on mobile).
export default function AppShell({ children }: { children: ReactNode }) {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="min-h-screen flex flex-col bg-slate-base">
            {/* atmospheric mesh */}
            <div aria-hidden="true" className="pointer-events-none fixed inset-0 bg-mesh-ember" />

            <Header clientName={i18n.language === 'ar' ? (user?.nameA || user?.name || '') : (user?.name || '')} />

            <main id="main" className="relative z-10 flex-1">
                {children}
            </main>

            <footer className="relative z-10 px-6 sm:px-12 py-6 mt-12 border-t border-slate-line/40">
                <div className="max-w-[1280px] mx-auto flex flex-wrap items-center justify-between gap-3 text-[11px] uppercase tracking-widest2 text-ink-dim">
                    <p>{t('brand.full')}</p>
                    <p>© {new Date().getFullYear()}</p>
                </div>
            </footer>
        </div>
    );
}

function Header({ clientName }: { clientName: string }) {
    const { t } = useTranslation();
    return (
        <header className="sticky top-0 z-20 backdrop-blur-md bg-slate-base/85 border-b border-slate-line/60">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-12 h-16 flex items-center gap-3 sm:gap-6">
                <Link to="/app" className="shrink-0">
                    <Logo size="sm" showWordmark={false} />
                </Link>

                <span aria-hidden="true" className="h-6 w-px bg-slate-line shrink-0" />

                <UnitSwitcher className="min-w-0 flex-1 sm:flex-none" />

                <nav className="hidden md:flex items-center gap-1 ml-auto">
                    <NavTab to="/app"           label={t('nav.dashboard')} end />
                    <NavTab to="/app/statement" label={t('nav.statement')} />
                    <NavTab to="/app/upload"    label={t('nav.upload')} />
                    <NavTab to="/app/profile"   label={t('nav.profile')} />
                </nav>

                {clientName && (
                    <span className="hidden lg:block md:ml-3 text-sm text-ink-muted max-w-[200px] truncate shrink-0">
                        {clientName}
                    </span>
                )}
            </div>

            {/* Mobile nav — secondary row */}
            <nav className="md:hidden flex items-center justify-start gap-1 px-4 pb-3 overflow-x-auto">
                <NavTab to="/app"           label={t('nav.dashboard')} end />
                <NavTab to="/app/statement" label={t('nav.statement')} />
                <NavTab to="/app/upload"    label={t('nav.upload')} />
                <NavTab to="/app/profile"   label={t('nav.profile')} />
            </nav>
        </header>
    );
}

function NavTab({ to, label, end }: { to: string; label: string; end?: boolean }) {
    return (
        <NavLink
            to={to}
            end={end}
            className={({ isActive }) =>
                `relative px-3 py-2 text-sm font-medium tracking-wide transition-colors
                 ${isActive ? 'text-ink-on' : 'text-ink-muted hover:text-ink-on'}`
            }
        >
            {({ isActive }) => (
                <>
                    {label}
                    {isActive && (
                        <span aria-hidden="true"
                              className="absolute left-3 right-3 -bottom-px h-px bg-ember" />
                    )}
                </>
            )}
        </NavLink>
    );
}
